import React, { useState, useCallback, useRef } from 'react';
import { 
  Plus, 
  Edit3, 
  Check, 
  RotateCcw,
  Sparkles,
  LayoutGrid
} from 'lucide-react';
import { WidgetCard } from './WidgetCard';
import { KPIWidget } from './KPIWidget';
import { BalanceWidget } from './BalanceWidget';
import { PnLWidget } from './PnLWidget';
import { CashWidget } from './CashWidget';
import { TaxWidget } from './TaxWidget';
import { RatioWidget } from './RatioWidget';
import { AccountWidget } from './AccountWidget';
import { MetricsWidget } from './MetricsWidget';
import { CreditScoreWidget } from './CreditScoreWidget';
import { AddWidgetModal } from './AddWidgetModal';
import { useDashboardStore } from '../../../stores/dashboardStore';
import type { DashboardWidget, WidgetType, WidgetSize } from '../../../types/dashboard-widgets';

interface WidgetGridProps {
  data: any;
  isLoading?: boolean;
}

// Render widget content based on type
const renderWidgetContent = (widget: DashboardWidget, data: any) => {
  switch (widget.type) {
    case 'kpi':
      return <KPIWidget config={widget.config} data={data} />;
    case 'balance':
      return <BalanceWidget data={data} />;
    case 'pnl':
      return <PnLWidget data={data} />;
    case 'cash':
      return <CashWidget data={data} />;
    case 'tax':
      return <TaxWidget data={data} />;
    case 'ratio':
      return <RatioWidget config={widget.config} data={data} />;
    case 'account':
      return <AccountWidget config={widget.config} data={data} />;
    case 'metrics':
      return <MetricsWidget data={data} />;
    case 'credit_score':
      return <CreditScoreWidget data={data} />;
    default:
      return <div className="text-muted-foreground text-sm">Widget inconnu</div>;
  }
};

export function WidgetGrid({ data, isLoading = false }: WidgetGridProps) {
  const { 
    layout, 
    isEditMode, 
    toggleEditMode, 
    removeWidget, 
    updateWidget,
    reorderWidgets,
    resetLayout 
  } = useDashboardStore();
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
  const [dragOverWidget, setDragOverWidget] = useState<string | null>(null);
  const dragRef = useRef<{ startX: number; startY: number } | null>(null);

  // Simple drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, widgetId: string) => {
    if (!isEditMode) return;
    
    setDraggedWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', widgetId);
    
    // Create ghost image
    const ghost = e.currentTarget.cloneNode(true) as HTMLElement;
    ghost.style.opacity = '0.8';
    ghost.style.transform = 'rotate(3deg)';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 20, 20);
    setTimeout(() => ghost.remove(), 0);
  }, [isEditMode]);

  const handleDragOver = useCallback((e: React.DragEvent, widgetId: string) => {
    e.preventDefault();
    if (draggedWidget && draggedWidget !== widgetId) {
      setDragOverWidget(widgetId);
    }
  }, [draggedWidget]);

  const handleDragLeave = useCallback(() => {
    setDragOverWidget(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetWidgetId: string) => {
    e.preventDefault();
    
    if (draggedWidget && draggedWidget !== targetWidgetId) {
      const currentOrder = layout.widgets.map(w => w.id);
      const draggedIndex = currentOrder.indexOf(draggedWidget);
      const targetIndex = currentOrder.indexOf(targetWidgetId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const newOrder = [...currentOrder];
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedWidget);
        reorderWidgets(newOrder);
      }
    }
    
    setDraggedWidget(null);
    setDragOverWidget(null);
  }, [draggedWidget, layout.widgets, reorderWidgets]);

  const handleDragEnd = useCallback(() => {
    setDraggedWidget(null);
    setDragOverWidget(null);
  }, []);

  const handleResize = useCallback((widgetId: string, newSize: WidgetSize) => {
    updateWidget(widgetId, { size: newSize });
  }, [updateWidget]);

  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
        {[...Array(8)].map((_, i) => (
          <div 
            key={i} 
            className={`
              bg-muted rounded-xl h-32
              ${i < 4 ? '' : 'md:col-span-2'}
            `}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-muted-foreground" />
          {isEditMode && (
            <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
              Mode édition
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {isEditMode && (
            <>
              <button
                onClick={resetLayout}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Réinitialiser
              </button>
              
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                Ajouter
              </button>
            </>
          )}
          
          <button
            onClick={toggleEditMode}
            className={`
              inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors
              ${isEditMode 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-muted hover:bg-muted/80 text-foreground'
              }
            `}
          >
            {isEditMode ? (
              <>
                <Check className="h-4 w-4" />
                Terminer
              </>
            ) : (
              <>
                <Edit3 className="h-4 w-4" />
                Personnaliser
              </>
            )}
          </button>
        </div>
      </div>

      {/* Widget Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[minmax(140px,auto)]">
        {layout.widgets.map((widget) => (
          <div
            key={widget.id}
            draggable={isEditMode}
            onDragStart={(e) => handleDragStart(e, widget.id)}
            onDragOver={(e) => handleDragOver(e, widget.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, widget.id)}
            onDragEnd={handleDragEnd}
            className={`
              transition-all duration-200
              ${dragOverWidget === widget.id ? 'scale-105 ring-2 ring-primary ring-offset-2' : ''}
              ${draggedWidget === widget.id ? 'opacity-50' : ''}
            `}
          >
            <WidgetCard
              widget={widget}
              isEditMode={isEditMode}
              isDragging={draggedWidget === widget.id}
              onRemove={() => removeWidget(widget.id)}
              onResize={(size) => handleResize(widget.id, size)}
            >
              {renderWidgetContent(widget, data)}
            </WidgetCard>
          </div>
        ))}

        {/* Empty state */}
        {layout.widgets.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Aucun widget</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Commencez à personnaliser votre tableau de bord
            </p>
            <button
              onClick={() => {
                if (!isEditMode) toggleEditMode();
                setShowAddModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Ajouter des widgets
            </button>
          </div>
        )}

        {/* Add widget placeholder in edit mode */}
        {isEditMode && layout.widgets.length > 0 && (
          <button
            onClick={() => setShowAddModal(true)}
            className="col-span-1 min-h-[140px] border-2 border-dashed border-muted-foreground/30 rounded-xl flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-all"
          >
            <Plus className="h-8 w-8" />
            <span className="text-sm font-medium">Ajouter un widget</span>
          </button>
        )}
      </div>

      {/* Add Widget Modal */}
      <AddWidgetModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)} 
      />
    </div>
  );
}
